import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, Loader2, Users } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

// Msg 412 — Zero-State UAT: positions are never auto-created. This dialog is
// the explicit Client Admin action that turns selected employee job titles
// into organizational Positions (duplicate-safe, no synthetic requirements).
export default function ImportPositionsDialog({ open, onClose, onImported }) {
  const { i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await clientApi.positionsImportPreview();
      setItems(d?.items || []);
    } catch (e) {
      toast.error(isES ? 'No se pudo cargar la vista previa' : 'Could not load preview', {
        description: formatApiError(e),
      });
      setItems([]);
    } finally { setLoading(false); }
  }, [isES]);

  useEffect(() => {
    if (open) { load(); setSelected(new Set()); setConfirming(false); }
  }, [open, load]);

  const importable = useMemo(() => items.filter((i) => !i.exists), [items]);

  const toggle = (title) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(title)) next.delete(title); else next.add(title);
    return next;
  });

  const toggleAll = () => setSelected((prev) => (
    prev.size === importable.length ? new Set() : new Set(importable.map((i) => i.title))
  ));

  const doImport = async () => {
    setImporting(true);
    try {
      const res = await clientApi.positionsImport([...selected]);
      toast.success(
        isES
          ? `${res.created} posición${res.created === 1 ? '' : 'es'} creada${res.created === 1 ? '' : 's'}`
          : `${res.created} position${res.created === 1 ? '' : 's'} created`,
      );
      onImported?.(res);
      onClose?.();
    } catch (e) {
      toast.error(isES ? 'No se pudo importar' : 'Import failed', { description: formatApiError(e) });
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="import-positions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" />
            {isES ? 'Importar posiciones desde cargos' : 'Import positions from job titles'}
          </DialogTitle>
          <DialogDescription>
            {isES
              ? 'Selecciona qué cargos de tus colaboradores se convertirán en Posiciones organizacionales. Estas posiciones alimentan el análisis de brechas de TalentMap y TalentCompetency. Nada se crea sin tu confirmación.'
              : 'Select which of your employees\' job titles should become organizational Positions. These power the TalentMap and TalentCompetency gap analysis. Nothing is created without your confirmation.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center text-sm text-muted-foreground py-8">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            {isES ? 'Analizando cargos de empleados…' : 'Analysing employee job titles…'}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="import-positions-empty">
            {isES
              ? 'Ningún colaborador tiene un cargo registrado todavía. Agrega cargos a tus empleados y vuelve a intentarlo.'
              : 'No employees have a job title recorded yet. Add job titles to your employees and try again.'}
          </p>
        ) : confirming ? (
          <div className="space-y-3" data-testid="import-positions-confirm">
            <p className="text-sm">
              {isES
                ? `Se crearán ${selected.size} posición${selected.size === 1 ? '' : 'es'} organizacional${selected.size === 1 ? '' : 'es'}:`
                : `${selected.size} organizational position${selected.size === 1 ? '' : 's'} will be created:`}
            </p>
            <ul className="text-sm border rounded-md divide-y max-h-56 overflow-y-auto">
              {[...selected].map((title) => (
                <li key={title} className="px-3 py-1.5">{title}</li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              {isES
                ? 'No se generarán requisitos de competencias automáticos. Podrás definirlos después.'
                : 'No automatic competency requirements will be generated. You can define them later.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {isES
                  ? `${items.length} cargo${items.length === 1 ? '' : 's'} único${items.length === 1 ? '' : 's'} encontrado${items.length === 1 ? '' : 's'}`
                  : `${items.length} unique job title${items.length === 1 ? '' : 's'} found`}
              </span>
              {importable.length > 0 && (
                <button type="button" onClick={toggleAll} className="text-primary hover:underline"
                        data-testid="import-positions-select-all">
                  {selected.size === importable.length
                    ? (isES ? 'Quitar todos' : 'Clear all')
                    : (isES ? 'Seleccionar todos' : 'Select all')}
                </button>
              )}
            </div>
            <ul className="border rounded-md divide-y max-h-72 overflow-y-auto" data-testid="import-positions-list">
              {items.map((it) => (
                <li key={it.title}
                    className={`flex items-center gap-3 px-3 py-2 ${it.exists ? 'opacity-60' : 'cursor-pointer hover:bg-muted/40'}`}
                    onClick={() => !it.exists && toggle(it.title)}
                    data-testid={`import-position-row-${it.title}`}>
                  <Checkbox
                    checked={it.exists || selected.has(it.title)}
                    disabled={it.exists}
                    onCheckedChange={() => toggle(it.title)}
                    data-testid={`import-position-check-${it.title}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{it.title}</p>
                    <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {isES
                        ? `${it.employee_count} empleado${it.employee_count === 1 ? '' : 's'}`
                        : `${it.employee_count} employee${it.employee_count === 1 ? '' : 's'}`}
                    </p>
                  </div>
                  {it.exists && (
                    <Badge variant="outline" className="text-xs">
                      {isES ? 'Ya existe' : 'Already exists'}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          {confirming ? (
            <>
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={importing}
                      data-testid="import-positions-back">
                {isES ? 'Volver' : 'Back'}
              </Button>
              <Button onClick={doImport} disabled={importing} data-testid="import-positions-confirm-btn">
                {importing && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                {isES ? 'Confirmar importación' : 'Confirm import'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} data-testid="import-positions-cancel">
                {isES ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button onClick={() => setConfirming(true)} disabled={selected.size === 0}
                      data-testid="import-positions-next">
                {isES
                  ? `Revisar (${selected.size})`
                  : `Review (${selected.size})`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
